import Auth0, { useAuth0 } from '@auth0/auth0-react'
import { useEffect, useRef, useState } from 'react'
import { USER_METADATA_KEY } from '../../constants/auth0'
import { User, UserDto } from '../../types/user'
import { jwtDecode } from 'jwt-decode'

interface UseAuthContext {
  accessToken?: string
  isAuthenticated: boolean
  isLoading: boolean
  logout: (options?: Auth0.LogoutOptions) => void
  user?: User
}

const useAuth = (): UseAuthContext => {
  const isMounted = useRef(false)
  const [accessToken, setAccessToken] = useState<string>('')
  const { isAuthenticated, isLoading, user, getAccessTokenSilently, logout } = useAuth0<UserDto>()
  const [permissions, setPermissions] = useState<Set<string>>(new Set())

  useEffect(() => {
    isMounted.current = true

    return () => {
      isMounted.current = false
    }
  }, [])

  useEffect(() => {
    if (accessToken || !isAuthenticated) return

    const fetchAccessToken = async () => {
      const token = await getAccessTokenSilently()
      const decodedToken: { permissions: string[] } = jwtDecode(token)

      if (decodedToken.permissions) {
        isMounted.current && setPermissions(new Set(decodedToken.permissions))
      }

      isMounted.current && setAccessToken(token)
    }

    fetchAccessToken()
  }, [accessToken, getAccessTokenSilently, isAuthenticated])

  let actualUser: User | undefined

  if (user) {
    const { [USER_METADATA_KEY]: data, updated_at, ...rest } = user
    const { user_metadata, roles } = data
    const { account_id, allowed_delivery_models } = user_metadata
    actualUser = {
      ...rest,
      account_id,
      roles,
      permissions,
      allowed_delivery_models,
      updated_at: new Date(updated_at),
    }
  }

  return {
    accessToken,
    isAuthenticated: Boolean(isAuthenticated && accessToken),
    isLoading,
    logout,
    user: actualUser,
  }
}

export { useAuth }
